import { useState, useEffect } from 'react'
import { apiClient } from '../utils/apiClient'
import { useAuth } from '../contexts/AuthContext'
import { canEditDatabases, canEditEnvironments, canEditProjects } from '../utils/orgCapabilities'

export const DEFAULT_DATA_CLASSIFICATION_PRESET_IDS = [
  'hipaa_safe_harbor',
  'pci_dss',
  'pipeda',
] as const

const ORG_SETTINGS_UPDATED = 'veilstream:organization-settings-updated'

/** Call after mutating org settings (e.g. Organization settings page) so all `useOrganizationSettings` consumers refetch. */
export function invalidateOrganizationSettings(): void {
  window.dispatchEvent(new Event(ORG_SETTINGS_UPDATED))
}

export type OrganizationMember = {
  user_id: number
  github_username: string
  role: string
}

export type OrganizationSettingsFlags = {
  organizationId: number | null
  organizationName: string
  canEditName: boolean
  canManageInvites: boolean
  canEditProjects: boolean
  canEditDatabases: boolean
  canEditEnvironments: boolean
  orgRole: string
  dataClassificationEnabled: boolean
  dataClassificationPresetIds: string[]
  qualityChecksEnabled: boolean
  prDemoVideosEnabled: boolean
  telemetryEnabled: boolean
  iosRunsEnabled: boolean
  rbacEnabled: boolean
  members: OrganizationMember[]
  loading: boolean
}

type OrganizationSettingsResponse = {
  organization_id?: number
  name?: string
  org_role?: string
  can_edit_name?: boolean
  can_edit_projects?: boolean
  can_edit_databases?: boolean
  can_edit_environments?: boolean
  data_classification_enabled?: boolean
  data_classification_preset_ids?: string[]
  quality_checks_enabled?: boolean
  playwright_runs_enabled?: boolean
  telemetry_enabled?: boolean
  ios_runs_enabled?: boolean
  rbac_enabled?: boolean
  members?: OrganizationMember[]
}

/**
 * Fetches /api/settings/organization. Used to gate org-level features outside the Settings page.
 * Refetches when `invalidateOrganizationSettings()` runs (silent refresh does not flip `loading` back to true).
 */
export function useOrganizationSettings(): OrganizationSettingsFlags {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [organizationId, setOrganizationId] = useState<number | null>(null)
  const [organizationName, setOrganizationName] = useState('')
  const [canEditName, setCanEditName] = useState(false)
  const [orgRole, setOrgRole] = useState('')
  const [canManageInvites, setCanManageInvites] = useState(false)
  const [canEditProjectsFlag, setCanEditProjectsFlag] = useState(false)
  const [canEditDatabasesFlag, setCanEditDatabasesFlag] = useState(false)
  const [canEditEnvironmentsFlag, setCanEditEnvironmentsFlag] = useState(false)
  const [dataClassificationEnabled, setDataClassificationEnabled] = useState(false)
  const [dataClassificationPresetIds, setDataClassificationPresetIds] = useState<string[]>([
    ...DEFAULT_DATA_CLASSIFICATION_PRESET_IDS,
  ])
  const [qualityChecksEnabled, setQualityChecksEnabled] = useState(false)
  const [prDemoVideosEnabled, setPrDemoVideosEnabled] = useState(false)
  const [telemetryEnabled, setTelemetryEnabled] = useState(false)
  const [iosRunsEnabled, setIosRunsEnabled] = useState(false)
  const [rbacEnabled, setRbacEnabled] = useState(false)
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) {
      setLoading(false)
      return
    }

    let cancelled = false
    let fetchId = 0

    const applyResponse = (d: OrganizationSettingsResponse) => {
      setOrganizationId(typeof d.organization_id === 'number' ? d.organization_id : null)
      setOrganizationName(typeof d.name === 'string' ? d.name : '')
      setCanEditName(Boolean(d.can_edit_name))
      setOrgRole(typeof d.org_role === 'string' ? d.org_role : '')
      setCanManageInvites(Boolean(d.can_edit_name))
      const role = typeof d.org_role === 'string' ? d.org_role : ''
      setCanEditProjectsFlag(d.can_edit_projects ?? canEditProjects(role))
      setCanEditDatabasesFlag(d.can_edit_databases ?? canEditDatabases(role))
      setCanEditEnvironmentsFlag(d.can_edit_environments ?? canEditEnvironments(role))
      setDataClassificationEnabled(Boolean(d.data_classification_enabled))
      if (Array.isArray(d.data_classification_preset_ids)) {
        setDataClassificationPresetIds(d.data_classification_preset_ids)
      } else {
        setDataClassificationPresetIds([...DEFAULT_DATA_CLASSIFICATION_PRESET_IDS])
      }
      setQualityChecksEnabled(Boolean(d.quality_checks_enabled))
      setPrDemoVideosEnabled(Boolean(d.playwright_runs_enabled))
      setTelemetryEnabled(Boolean(d.telemetry_enabled))
      setIosRunsEnabled(Boolean(d.ios_runs_enabled))
      setRbacEnabled(Boolean(d.rbac_enabled))
      setMembers(Array.isArray(d.members) ? d.members : [])
    }

    const load = async (opts: { showLoading: boolean }) => {
      const id = ++fetchId
      if (opts.showLoading) setLoading(true)
      try {
        const d = await apiClient.get<OrganizationSettingsResponse>('/api/settings/organization', undefined, {
          quiet: true,
        })
        if (cancelled || id !== fetchId) return
        applyResponse(d)
      } catch {
        if (cancelled || id !== fetchId) return
        setOrganizationId(null)
        setOrganizationName('')
        setCanEditName(false)
        setOrgRole('')
        setCanManageInvites(false)
        setCanEditProjectsFlag(false)
        setCanEditDatabasesFlag(false)
        setCanEditEnvironmentsFlag(false)
        setDataClassificationEnabled(false)
        setDataClassificationPresetIds([...DEFAULT_DATA_CLASSIFICATION_PRESET_IDS])
        setQualityChecksEnabled(false)
        setPrDemoVideosEnabled(false)
        setTelemetryEnabled(false)
        setIosRunsEnabled(false)
        setRbacEnabled(false)
        setMembers([])
      } finally {
        if (!cancelled && opts.showLoading) setLoading(false)
      }
    }

    void load({ showLoading: true })

    const onInvalidate = () => {
      void load({ showLoading: false })
    }
    window.addEventListener(ORG_SETTINGS_UPDATED, onInvalidate)
    return () => {
      cancelled = true
      window.removeEventListener(ORG_SETTINGS_UPDATED, onInvalidate)
    }
  }, [isAuthenticated, authLoading])

  return {
    organizationId,
    organizationName,
    canEditName,
    canManageInvites,
    canEditProjects: canEditProjectsFlag,
    canEditDatabases: canEditDatabasesFlag,
    canEditEnvironments: canEditEnvironmentsFlag,
    orgRole,
    dataClassificationEnabled,
    dataClassificationPresetIds,
    qualityChecksEnabled,
    prDemoVideosEnabled,
    telemetryEnabled,
    iosRunsEnabled,
    rbacEnabled,
    members,
    loading,
  }
}
